import React, { createContext, useContext, useState, useEffect } from 'react';
import enTranslations from '../translations/en.json';
import esTranslations from '../translations/es.json';
import zhTranslations from '../translations/zh.json';
import hiTranslations from '../translations/hi.json';
import frTranslations from '../translations/fr.json';
import ptTranslations from '../translations/pt.json';
import viTranslations from '../translations/vi.json';
import jaTranslations from '../translations/ja.json';

const LanguageContext = createContext();

const translationData = {
  en: enTranslations,
  es: esTranslations,
  zh: zhTranslations,
  hi: hiTranslations,
  fr: frTranslations,
  pt: ptTranslations,
  vi: viTranslations,
  ja: jaTranslations
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to English
    const savedLanguage = localStorage.getItem('mecharena-language') || 'en';
    // Ensure the saved language exists in translationData, fallback to 'en'
    return translationData[savedLanguage] ? savedLanguage : 'en';
  });

  const [translations, setTranslations] = useState(() => {
    return translationData[language] || translationData.en;
  });

  useEffect(() => {
    // Save language preference to localStorage
    const translationsToUse = translationData[language] || translationData.en;
    if (translationsToUse) {
      localStorage.setItem('mecharena-language', language);
      setTranslations(translationsToUse);
    } else {
      console.warn(`Translation data not found for language: ${language}, falling back to English`);
      setLanguage('en');
    }
  }, [language]);

  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    if (typeof value !== 'string') {
      console.warn(`Translation key "${key}" not found`);
      return key;
    }
    
    // Replace parameters in the translation string
    return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
      return params[paramKey] || match;
    });
  };

  const changeLanguage = (newLanguage) => {
    if (translationData[newLanguage]) {
      setLanguage(newLanguage);
    } else {
      console.warn(`Language "${newLanguage}" not found in translationData`);
    }
  };

  const value = {
    language,
    changeLanguage,
    t,
    availableLanguages: [
      { code: 'en', name: 'English', flag: '🇺🇸' },
      { code: 'es', name: 'Español', flag: '🇪🇸' },
      { code: 'zh', name: '中文', flag: '🇨🇳' },
      { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
      { code: 'fr', name: 'Français', flag: '🇫🇷' },
      { code: 'pt', name: 'Português', flag: '🇵🇹' },
      { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
      { code: 'ja', name: '日本語', flag: '🇯🇵' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
